/**
 * Add a Dedicated (role-targeted) Master Resume — two-step modal.
 *
 * Step 1 collects the job title / keywords. "Next" kicks off the deterministic
 * search + tag aggregation (async, can take a good number of seconds) and moves
 * straight on to step 2, where the user names the URL path while the search
 * status streams in. Saving before the search finishes is fine — the server
 * queues the generation for the moment aggregation lands.
 *
 * PORTALED to document.body on purpose (LEARNINGS: a `fixed inset-0` overlay
 * rendered inside a card positions against that card, not the viewport).
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2, Search, X } from 'lucide-react';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { api } from '../services/api';
import type { DedicatedMaster, DedicatedMasterStatusResponse } from '../types';

interface Props {
  isOpen: boolean;
  resumename: string;
  /** Lowercased username — builds the fixed URL prefix. */
  username: string;
  /** Role-targeted paths already on THIS resume — a real collision. */
  existingPaths: string[];
  /**
   * Role-targeted paths on the user's OTHER resumes. Allowed — the most
   * recently generated one is what the URL shows — so this is only a note.
   */
  sharedPaths?: string[];
  onClose: () => void;
  onCreated: (dm: DedicatedMaster) => void;
  /** Server said status != 'active' — the parent disables the Add control. */
  onAccessDenied?: () => void;
}

const PATH_MAX = 60;
// Mirrors the server-side reject list; '.' can't be typed into the box, so only
// the dotless names are reachable here.
const RESERVED_PATHS = [
  'assets', '_global', '_exports', 'favicon.ico', 'robots.txt', 'sitemap.xml', 'index.html',
];

const STATUS_POLL_MS = 3000;

// Same rule the server uses to derive the default path: a run of URL-unsafe
// characters collapses to one '_' (so "solution engineer" types as
// "solution_engineer"), then the whole thing is lowercased because a master
// resume URL is published all-lowercase and read case-insensitively
// (utils/masterUrls; shared/master_urls.py). Trailing separators are left
// alone while typing.
function sanitizePath(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/_+/g, '_').toLowerCase().slice(0, PATH_MAX);
}

/** Why this path can't be used, or null when it's fine. */
function pathProblem(path: string, existingPaths: string[]): string | null {
  if (!path) return 'Enter a path for this resume.';
  if (RESERVED_PATHS.includes(path.toLowerCase())) return `"${path}" is reserved — pick another path.`;
  if (existingPaths.some(p => p.toLowerCase() === path.toLowerCase())) {
    return `You already publish a resume at /${path}.`;
  }
  return null;
}

/** Another RESUME targets this path — allowed, but worth saying out loud. */
function sharedPathNote(path: string, sharedPaths: string[]): string | null {
  if (!path) return null;
  return sharedPaths.some(p => p.toLowerCase() === path.toLowerCase())
    ? `Another resume also targets /${path} — the most recently generated one is shown.`
    : null;
}

function accessMessage(): string {
  return 'Role-targeted master resumes are available on Active accounts.';
}

function errorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { status?: number; data?: { message?: string } } };
  if (e?.response?.status === 403) return accessMessage();
  return e?.response?.data?.message || fallback;
}

export function DedicatedMasterModal({
  isOpen, resumename, username, existingPaths, sharedPaths = [],
  onClose, onCreated, onAccessDenied,
}: Props) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [keywords, setKeywords] = useState('');
  const [searching, setSearching] = useState(false);
  const [jobid, setJobid] = useState<string | null>(null);
  const [expandedKeywords, setExpandedKeywords] = useState<string[]>([]);
  const [path, setPath] = useState('');
  const [status, setStatus] = useState<DedicatedMasterStatusResponse | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<{ cancelled: boolean } | null>(null);
  // Set once the entry is created so Cancel/unmount doesn't delete the job the
  // saved entry now points at.
  const savedRef = useRef(false);

  useEscapeClose(handleCancel, isOpen);
  useFocusTrap(modalRef, isOpen);

  // Fresh state every time the modal is opened.
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setKeywords('');
    setSearching(false);
    setJobid(null);
    setExpandedKeywords([]);
    setPath('');
    setStatus(null);
    setStatusError(null);
    setSaving(false);
    setError(null);
    savedRef.current = false;
  }, [isOpen]);

  // Poll the aggregation until it settles. Started when the search is dispatched.
  useEffect(() => {
    if (!isOpen || !jobid) return;
    const ctrl = { cancelled: false };
    pollRef.current = ctrl;
    const tick = async () => {
      if (ctrl.cancelled) return;
      try {
        const data = await api.getDedicatedMasterStatus(resumename, jobid);
        if (ctrl.cancelled) return;
        setStatus(data);
        setStatusError(null);
        if (data.agg_state !== 'searching') return;
      } catch (err) {
        if (ctrl.cancelled) return;
        setStatusError(errorMessage(err, 'Lost contact with the search — still trying.'));
      }
      if (ctrl.cancelled) return;
      setTimeout(tick, STATUS_POLL_MS);
    };
    setTimeout(tick, STATUS_POLL_MS);
    return () => { ctrl.cancelled = true; };
  }, [isOpen, jobid, resumename]);

  async function handleNext() {
    const text = keywords.trim();
    if (!text) return;
    setSearching(true);
    setError(null);
    try {
      const res = await api.searchDedicatedMaster(resumename, text);
      setJobid(res.jobid);
      setExpandedKeywords(res.expanded_keywords || []);
      setPath(sanitizePath(res.default_path || ''));
      setStep(2);
    } catch (err) {
      const e = err as { response?: { status?: number } };
      if (e?.response?.status === 403) onAccessDenied?.();
      setError(errorMessage(err, 'Could not start the search. Please try again.'));
    } finally {
      setSearching(false);
    }
  }

  async function handleSave() {
    if (!jobid) return;
    setSaving(true);
    setError(null);
    try {
      const dm = await api.createDedicatedMaster(resumename, { jobid, path, keywords: keywords.trim() });
      savedRef.current = true;
      if (pollRef.current) pollRef.current.cancelled = true;
      onCreated(dm);
    } catch (err) {
      const e = err as { response?: { status?: number } };
      if (e?.response?.status === 403) onAccessDenied?.();
      setError(errorMessage(err, 'Could not add this resume. Please try again.'));
      setSaving(false);
    }
  }

  function handleCancel() {
    if (pollRef.current) pollRef.current.cancelled = true;
    // Best-effort cleanup of the synthetic job nobody saved.
    if (jobid && !savedRef.current) {
      api.cancelDedicatedMasterSearch(resumename, jobid).catch(() => { /* best-effort */ });
    }
    onClose();
  }

  if (!isOpen) return null;

  const problem = step === 2 ? pathProblem(path, existingPaths) : null;
  const shared = step === 2 && !problem ? sharedPathNote(path, sharedPaths) : null;
  const aggState = status?.agg_state ?? null;
  const searchFailed = aggState === 'no_matches' || aggState === 'error';
  const canSave = step === 2 && !!jobid && !problem && !searchFailed && !saving;
  const previewUrl = `https://${username || 'your_name'}.skillsnap.me/${path}`;

  const body = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Add a role-targeted master resume"
      data-testid="dedicated-master-modal"
    >
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between p-4 border-b">
          <div className="flex items-start gap-2">
            <Search className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold">Add a role-targeted master resume</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {step === 1
                  ? 'One resume aimed at a type of role — perfect for a headhunter who asks for "your solution engineering resume".'
                  : 'Pick the URL this resume will live at.'}
              </p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            className="p-1 hover:bg-gray-100 rounded flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto space-y-3">
          {step === 1 && (
            <>
              <label className="block text-sm font-medium text-gray-700" htmlFor="dm-keywords">
                Job title or keywords
              </label>
              <textarea
                id="dm-keywords"
                value={keywords}
                autoFocus
                rows={3}
                onChange={(e) => setKeywords(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder={'"solution engineer", "forward deployed engineer", "solution architect"'}
                autoComplete="off"
                data-lpignore="true"
                data-form-type="other"
                data-1p-ignore="true"
              />
              <p className="text-xs text-gray-500">
                SkillSnap finds every job whose title matches what you type, then averages their
                requirements and prerequisites into one role profile — and tailors this resume to
                that profile rather than to any single company. Any synonym sets you&apos;ve defined
                are searched too, so related titles come along automatically.
              </p>
            </>
          )}

          {step === 2 && (
            <>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700" htmlFor="dm-path">
                  URL path
                </label>
                <div className="flex items-center gap-0">
                  <span className="inline-flex items-center px-3 py-2 rounded-l-lg border border-r-0 border-gray-300 bg-gray-100 text-gray-500 text-sm select-all">
                    https://{username || 'your_name'}.skillsnap.me/
                  </span>
                  <input
                    id="dm-path"
                    type="text"
                    value={path}
                    autoFocus
                    onChange={(e) => setPath(sanitizePath(e.target.value))}
                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-r-lg text-sm"
                    placeholder="solution_engineer"
                    autoComplete="off"
                    data-lpignore="true"
                    data-form-type="other"
                    data-1p-ignore="true"
                  />
                </div>
                {problem ? (
                  <div className="flex items-center gap-1 text-xs text-amber-600">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                    <span>{problem}</span>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 break-all">{previewUrl}</p>
                )}
                {shared && (
                  <p className="text-xs text-gray-500" data-testid="dm-shared-path-note">
                    {shared}
                  </p>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1">
                {aggState === null && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Searching jobs&hellip;</span>
                  </div>
                )}
                {aggState === 'searching' && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Searching jobs&hellip; this can take a little while.</span>
                  </div>
                )}
                {aggState === 'done' && (
                  <p className="text-sm text-green-700">
                    {typeof status?.matched_jobs === 'number'
                      ? `${status.matched_jobs} matching ${status.matched_jobs === 1 ? 'job' : 'jobs'} found`
                      : 'Search complete'}
                    {typeof status?.tag_count === 'number' && ` — ${status.tag_count} shared tags`}.
                  </p>
                )}
                {aggState === 'no_matches' && (
                  <p className="text-sm text-amber-700">
                    No jobs in the library have a title matching that. Try a broader or more common
                    title (or add a synonym set for the wording you use), then start over.
                  </p>
                )}
                {aggState === 'error' && (
                  <p className="text-sm text-red-600">
                    {status?.message || 'The search ran into a problem. Please cancel and try again.'}
                  </p>
                )}
                {statusError && aggState !== 'error' && (
                  <p className="text-xs text-gray-500">{statusError}</p>
                )}
                {expandedKeywords.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    <span className="text-xs text-gray-500 mr-1">Searching titles for:</span>
                    {expandedKeywords.map((k) => (
                      <span key={k} className="text-xs bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded">
                        {k}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {(aggState === null || aggState === 'searching') && (
                <p className="text-xs text-gray-500">
                  You can save now — the resume starts generating the moment the search finishes.
                </p>
              )}
            </>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t">
          <button
            onClick={handleCancel}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
            disabled={saving}
          >
            Cancel
          </button>
          {step === 1 ? (
            <button
              onClick={handleNext}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              disabled={!keywords.trim() || searching}
              data-uat="dedicated-master-next"
            >
              {searching && <Loader2 className="w-4 h-4 animate-spin" />}
              {searching ? 'Starting…' : 'Next'}
            </button>
          ) : (
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              disabled={!canSave}
              data-uat="dedicated-master-save"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}

export default DedicatedMasterModal;
